import { useCallback, useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { toast } from "sonner";
import {
  adminListCareers, adminCreateCareer, adminUpdateCareer,
  adminToggleCareer, adminDeleteCareer,
} from "../../lib/api";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Textarea } from "../../components/ui/textarea";
import { Label } from "../../components/ui/label";
import { Badge } from "../../components/ui/badge";
import { Switch } from "../../components/ui/switch";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle,
  DialogDescription, DialogFooter,
} from "../../components/ui/dialog";
import { Loader2, Plus, Trash2, Pencil, Briefcase } from "lucide-react";

const EMPTY = {
  title: "", dept: "", type: "Full-time", location: "",
  summary: "", responsibilities: [""], requirements: [""],
  active: true, order: 0,
};

function asLines(list) {
  return (list || []).join("\n");
}
function fromLines(text) {
  return (text || "").split("\n").map((s) => s.trim()).filter(Boolean);
}

export default function CareersManager() {
  const [roles, setRoles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null); // null | 'new' | role object
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState(EMPTY);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const data = await adminListCareers();
      setRoles(Array.isArray(data) ? data : []);
    } catch {
      toast.error("Could not load roles");
    } finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const openNew = () => {
    setEditing("new");
    setForm({ ...EMPTY, order: roles.length });
  };
  const openEdit = (r) => {
    setEditing(r);
    setForm({
      title: r.title || "", dept: r.dept || "", type: r.type || "Full-time",
      location: r.location || "", summary: r.summary || "",
      responsibilities: r.responsibilities?.length ? r.responsibilities : [""],
      requirements: r.requirements?.length ? r.requirements : [""],
      active: r.active ?? true, order: r.order ?? 0,
    });
  };

  const save = async () => {
    if (!form.title.trim() || !form.dept.trim()) {
      toast.error("Title and department are required."); return;
    }
    setSaving(true);
    const payload = {
      ...form,
      responsibilities: fromLines(asLines(form.responsibilities)),
      requirements: fromLines(asLines(form.requirements)),
      order: Number(form.order) || 0,
    };
    try {
      if (editing === "new") {
        await adminCreateCareer(payload);
        toast.success("Role created");
      } else {
        await adminUpdateCareer(editing.id, payload);
        toast.success("Role updated");
      }
      setEditing(null);
      load();
    } catch (err) {
      const d = err?.response?.data?.detail;
      toast.error(typeof d === "string" ? d : "Save failed");
    } finally { setSaving(false); }
  };

  const toggle = async (r) => {
    setRoles((prev) => prev.map((x) => (x.id === r.id ? { ...x, active: !x.active } : x)));
    try { await adminToggleCareer(r.id); }
    catch { toast.error("Update failed"); load(); }
  };

  const remove = async (r) => {
    if (!window.confirm(`Delete role "${r.title}"? This cannot be undone.`)) return;
    try { await adminDeleteCareer(r.id); toast.success("Role deleted"); load(); }
    catch { toast.error("Delete failed"); }
  };

  return (
    <div data-testid="careers-manager">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h2 className="font-display text-xl font-bold tracking-tight">Job openings</h2>
          <p className="text-sm text-muted-foreground mt-1">Create, edit and publish roles that appear on the public Careers page.</p>
        </div>
        <Button data-testid="new-role-btn" onClick={openNew} className="rounded-full bg-brand-gradient text-white border-0 hover:opacity-90">
          <Plus className="h-4 w-4 mr-1" /> New role
        </Button>
      </div>

      <div className="rounded-2xl border border-border/10 bg-card overflow-hidden">
        {loading ? (
          <div className="p-10 grid place-items-center"><Loader2 className="h-6 w-6 animate-spin text-primary" /></div>
        ) : roles.length === 0 ? (
          <div className="p-10 text-center text-muted-foreground">No roles yet. Click &quot;New role&quot; to add one.</div>
        ) : (
          <ul className="divide-y divide-border/10">
            <AnimatePresence initial={false}>
              {roles.map((r) => (
                <motion.li
                  key={r.id}
                  layout
                  initial={{ opacity: 0, y: 6 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0 }}
                  data-testid={`role-item-${r.id}`}
                  className="p-5 flex flex-col md:flex-row md:items-center gap-4"
                >
                  <div className="flex-1 min-w-0">
                    <div className="flex flex-wrap items-center gap-2">
                      <span className="font-display text-base font-bold tracking-tight">{r.title}</span>
                      <Badge variant="outline" className="border-primary/30 text-primary">{r.dept}</Badge>
                      {!r.active && <Badge variant="outline" className="border-border/30 text-muted-foreground">Draft</Badge>}
                    </div>
                    <div className="mt-1 text-xs text-muted-foreground flex flex-wrap gap-x-4 gap-y-1">
                      <span><Briefcase className="inline h-3 w-3 mr-1" />{r.type}</span>
                      <span>{r.location}</span>
                      <span>ID: {r.id}</span>
                    </div>
                    {r.summary && <p className="mt-2 text-sm text-muted-foreground line-clamp-2">{r.summary}</p>}
                  </div>
                  <div className="flex items-center gap-3 shrink-0">
                    <div className="flex items-center gap-2">
                      <Switch
                        data-testid={`role-toggle-${r.id}`}
                        checked={!!r.active}
                        onCheckedChange={() => toggle(r)}
                      />
                      <span className="text-xs text-muted-foreground min-w-[3.5rem]">{r.active ? "Live" : "Hidden"}</span>
                    </div>
                    <Button data-testid={`role-edit-${r.id}`} variant="outline" size="sm" className="rounded-full border-border/20" onClick={() => openEdit(r)}>
                      <Pencil className="h-3.5 w-3.5 mr-1" /> Edit
                    </Button>
                    <Button data-testid={`role-delete-${r.id}`} variant="ghost" size="icon" className="text-muted-foreground hover:text-destructive" onClick={() => remove(r)}>
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </div>
                </motion.li>
              ))}
            </AnimatePresence>
          </ul>
        )}
      </div>

      {/* Editor Dialog */}
      <Dialog open={editing !== null} onOpenChange={(o) => { if (!o) setEditing(null); }}>
        <DialogContent className="rounded-3xl max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="font-display">{editing === "new" ? "New role" : `Edit role — ${editing?.title}`}</DialogTitle>
            <DialogDescription>Fill in the fields below. Put one item per line for responsibilities and requirements.</DialogDescription>
          </DialogHeader>
          <div className="grid gap-4 mt-2">
            <div className="grid md:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="r-title">Title *</Label>
                <Input data-testid="role-title" id="r-title" value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} className="rounded-xl h-11" />
              </div>
              <div className="space-y-2">
                <Label htmlFor="r-dept">Department *</Label>
                <Input data-testid="role-dept" id="r-dept" value={form.dept} onChange={(e) => setForm({ ...form, dept: e.target.value })} placeholder="Creative, Growth, Events…" className="rounded-xl h-11" />
              </div>
              <div className="space-y-2">
                <Label htmlFor="r-type">Type</Label>
                <Input data-testid="role-type" id="r-type" value={form.type} onChange={(e) => setForm({ ...form, type: e.target.value })} placeholder="Full-time / Contract / Internship" className="rounded-xl h-11" />
              </div>
              <div className="space-y-2">
                <Label htmlFor="r-loc">Location</Label>
                <Input data-testid="role-location" id="r-loc" value={form.location} onChange={(e) => setForm({ ...form, location: e.target.value })} placeholder="Gurugram · On-site" className="rounded-xl h-11" />
              </div>
            </div>
            <div className="space-y-2">
              <Label htmlFor="r-sum">Short summary</Label>
              <Textarea data-testid="role-summary" id="r-sum" value={form.summary} onChange={(e) => setForm({ ...form, summary: e.target.value })} className="rounded-xl min-h-[80px]" />
            </div>
            <div className="space-y-2">
              <Label htmlFor="r-resp">Responsibilities <span className="text-muted-foreground text-xs">(one per line)</span></Label>
              <Textarea data-testid="role-responsibilities" id="r-resp" value={asLines(form.responsibilities)} onChange={(e) => setForm({ ...form, responsibilities: fromLines(e.target.value) })} className="rounded-xl min-h-[120px]" />
            </div>
            <div className="space-y-2">
              <Label htmlFor="r-req">Requirements <span className="text-muted-foreground text-xs">(one per line)</span></Label>
              <Textarea data-testid="role-requirements" id="r-req" value={asLines(form.requirements)} onChange={(e) => setForm({ ...form, requirements: fromLines(e.target.value) })} className="rounded-xl min-h-[120px]" />
            </div>
            <div className="grid md:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="r-order">Order (lower = higher on page)</Label>
                <Input data-testid="role-order" id="r-order" type="number" value={form.order} onChange={(e) => setForm({ ...form, order: e.target.value })} className="rounded-xl h-11" />
              </div>
              <div className="flex items-center gap-3 pt-6">
                <Switch data-testid="role-active" checked={!!form.active} onCheckedChange={(v) => setForm({ ...form, active: v })} />
                <span className="text-sm">{form.active ? "Live on Careers page" : "Hidden (draft)"}</span>
              </div>
            </div>
          </div>
          <DialogFooter className="mt-4">
            <Button variant="outline" className="rounded-full border-border/20" onClick={() => setEditing(null)}>Cancel</Button>
            <Button data-testid="role-save" onClick={save} disabled={saving} className="rounded-full bg-brand-gradient text-white border-0 hover:opacity-90">
              {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : editing === "new" ? "Create role" : "Save changes"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
